import { CandidateRating } from '../../types';
import {
  useCareers, useCreateCareer, useUpdateCareer, useDeleteCareer
} from '../../hooks/useCareers';
import {
  useCandidates, useCreateCandidate, useUpdateCandidate
} from '../../hooks/useCandidates';
import { useAssessmentPoints } from '../../hooks/useAssessmentPoints';
import { Job, Application, convertEmploymentTypeFromAPI, convertEmploymentTypeToAPI } from './types';

export function useHiringData() {
  const { data: apiCareers = [] } = useCareers();
  const createCareerMutation = useCreateCareer();
  const updateCareerMutation = useUpdateCareer();
  const deleteCareerMutation = useDeleteCareer();

  const { data: apiCandidates = [] } = useCandidates();
  const createCandidateMutation = useCreateCandidate();
  const updateCandidateMutation = useUpdateCandidate();

  const { data: assessmentPoints = [] } = useAssessmentPoints();

  const jobs: Job[] = apiCareers.map((career) => ({
    id: career.documentId || career.id,
    title: career.title,
    department: career.department,
    location: career.location,
    type: convertEmploymentTypeFromAPI(career.employmentType),
    salary: typeof career.salaryRange === 'string' ? career.salaryRange : 'Negotiable',
    postedDate: career.createdAt?.split('T')[0] || '',
    deadline: career.applicationDeadline || '',
    status: career.isActive ? 'Open' : 'Closed',
    applicants: 0,
    description: career.description,
    requirements: career.requirements ? (typeof career.requirements === 'string' ? JSON.parse(career.requirements) : career.requirements) : [],
    documentId: career.documentId
  } as any));

  const applications: Application[] = apiCandidates.map((candidate) => ({
    id: candidate.documentId || candidate.id,
    jobId: candidate.appliedPosition || '',
    candidateName: `${candidate.firstName} ${candidate.lastName}`,
    email: candidate.email,
    phone: candidate.phone,
    appliedDate: candidate.appliedDate || '',
    status: candidate.status === 'new' ? 'Pending' :
           candidate.status === 'under_review' ? 'Reviewed' :
           candidate.status === 'interview_scheduled' ? 'Interview' :
           candidate.status === 'hired' ? 'Hired' : 'Rejected',
    resume: candidate.resumeUrl || '',
    rating: [],
    totalRating: candidate.totalRating || 0,
    evaluatedBy: candidate.assignedManagerName,
    evaluatedAt: candidate.interviewDate,
    notes: candidate.notes,
    documentId: candidate.documentId
  } as any));

  const deleteJob = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this job posting?')) {
      await deleteCareerMutation.execute(id);
    }
  };

  const saveJob = async (jobData: Omit<Job, 'id' | 'applicants'>, editingJob: Job | null) => {
    if (editingJob) {
      await updateCareerMutation.execute({
        id: editingJob.id,
        data: {
          title: jobData.title,
          department: jobData.department,
          location: jobData.location,
          employmentType: convertEmploymentTypeToAPI(jobData.type),
          salaryRange: jobData.salary,
          applicationDeadline: jobData.deadline,
          isActive: jobData.status === 'Open',
          description: jobData.description,
          requirements: JSON.stringify(jobData.requirements)
        }
      });
    } else {
      await createCareerMutation.execute({
        title: jobData.title,
        arabicTitle: jobData.title,
        department: jobData.department,
        location: jobData.location,
        employmentType: convertEmploymentTypeToAPI(jobData.type),
        experienceLevel: 'mid_level',
        salaryRange: jobData.salary,
        applicationDeadline: jobData.deadline,
        isActive: jobData.status === 'Open',
        description: jobData.description,
        requirements: JSON.stringify(jobData.requirements),
        contactEmail: 'hr@petromarine.com',
        postedBy: 'Admin'
      });
    }
  };

  const calculateTotalRating = (ratings: CandidateRating[]): number => {
    if (ratings.length === 0) return 0;
    const totalWeightedScore = ratings.reduce((sum, rating) => {
      const point = assessmentPoints.find(p => p.id === rating.pointId!);
      return sum + (rating.score * (point?.weight || 1));
    }, 0);
    const totalWeight = ratings.reduce((sum, rating) => {
      const point = assessmentPoints.find(p => p.id === rating.pointId!);
      return sum + (point?.weight || 1);
    }, 0);
    return Math.round((totalWeightedScore / totalWeight) * 10) / 10;
  };

  const saveRating = async (ratings: CandidateRating[], applicationId: string) => {
    const totalRating = calculateTotalRating(ratings);
    await updateCandidateMutation.execute({
      id: applicationId,
      data: { totalRating, interviewDate: new Date().toISOString() }
    });
  };

  const submitCandidate = async (
    data: any,
    editingApplication: Application | null,
    selectedJobForApply: Job | null
  ) => {
    if (editingApplication) {
      await updateCandidateMutation.execute({
        id: editingApplication.id,
        data: {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email || '',
          phone: data.phone || '',
          status: data.status,
          notes: data.notes || '',
          totalRating: data.totalRating || 0
        }
      });
    } else if (selectedJobForApply) {
      await createCandidateMutation.execute({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email || '',
        phone: data.phone || '',
        appliedDate: new Date().toISOString().split('T')[0],
        appliedPosition: selectedJobForApply.title,
        status: 'new',
        nationality: data.nationality || '',
        dateOfBirth: data.dateOfBirth || '',
        source: data.source || 'website',
        experience: data.experience || 0,
        education: data.education || '',
        certificates: data.certificates || [],
        languages: data.languages || [],
        previousCompany: data.previousCompany || '',
        expectedSalary: data.expectedSalary || 0,
        availableFrom: data.availableFrom || '',
        coverLetter: data.coverLetter || '',
        assignedManagerId: '',
        assignedManagerName: '',
        managerFeedback: '',
        notes: data.notes || '',
        rating: [],
        totalRating: 0
      });
    }
  };

  const hiringAssessmentPoints = assessmentPoints.filter(point =>
    point.isActive && (point.applicableFor === 'hiring' || point.applicableFor === 'both')
  );

  return {
    jobs,
    applications,
    assessmentPoints: hiringAssessmentPoints,
    deleteJob,
    saveJob,
    saveRating,
    submitCandidate,
  };
}
